import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axiosInstance from "@/services/api/axios";

interface Settlement {
  id: string;
  merchant_name: string;
  currency_code: string;
  status: string;
  amount: string;
  commission: string;
  final_amount: string;
  wallet: string;
  date_create: string;
  conversion_rate: string | null;
  amount_in_usdt: string | null;
  final_amount_in_usdt: string | null;
}

export default function Settlements() {
  const [items, setItems] = useState<Settlement[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("");
  const currencies = Array.from(new Set(items.map((item) => item.currency_code))).sort();
  const filteredItems = items.filter((item) => { const needle = search.trim().toLowerCase(); return (!needle || [item.id, item.merchant_name, item.wallet].some((value) => value.toLowerCase().includes(needle))) && (!status || item.status === status) && (!currency || item.currency_code === currency); });
  const load = useCallback(async () => {
    try {
      setItems((await axiosInstance.get("/settlements", { params: { search: search || undefined, status: status || undefined, currency: currency || undefined } })).data);
    } catch {
      setError("Не удалось загрузить settlements");
    }
  }, [search, status, currency]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 300);
    return () => window.clearTimeout(timer);
  }, [load]);
  const finalize = async (id: string, action: "success" | "decline") => {
    try {
      setBusy(id);
      setError("");
      await axiosInstance.post(`/settlements/${id}/${action}`);
      await load();
    } catch (e: any) {
      setError(
        e.response?.data?.detail || "Не удалось изменить статус settlement",
      );
    } finally {
      setBusy("");
    }
  };
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Settlements
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: "column", md: "row" }} spacing={2}><TextField fullWidth label="Поиск по мерчанту, кошельку или ID" value={search} onChange={(event) => setSearch(event.target.value)} /><FormControl sx={{ minWidth: 180 }}><InputLabel>Статус</InputLabel><Select value={status} label="Статус" onChange={(event) => setStatus(event.target.value)}><MenuItem value="">Все</MenuItem>{["ACCEPTED", "SUCCESS", "DECLINED"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</Select></FormControl><FormControl sx={{ minWidth: 160 }}><InputLabel>Валюта</InputLabel><Select value={currency} label="Валюта" onChange={(event) => setCurrency(event.target.value)}><MenuItem value="">Все</MenuItem>{currencies.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</Select></FormControl><Button onClick={() => { setSearch(""); setStatus(""); setCurrency(""); }}>Сбросить</Button></Stack></Paper>
      <Paper sx={{ p: 2, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Создан</TableCell>
                <TableCell>Мерчант</TableCell>
                <TableCell>Валюта</TableCell>
                <TableCell align="right">Сумма</TableCell>
                <TableCell align="right">Комиссия</TableCell>
                <TableCell align="right">Курс к USDT</TableCell>
                <TableCell align="right">Сумма в USDT</TableCell>
                <TableCell align="right">К выплате в USDT</TableCell>
                <TableCell align="right">К выплате</TableCell>
                <TableCell>Кошелёк</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {new Date(item.date_create).toLocaleString()}
                  </TableCell>
                  <TableCell>{item.merchant_name}</TableCell>
                  <TableCell>{item.currency_code}</TableCell>
                  <TableCell align="right">{item.amount}</TableCell>
                  <TableCell align="right">{item.commission}%</TableCell>
                  <TableCell align="right">
                    {item.conversion_rate ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {item.amount_in_usdt ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {item.final_amount_in_usdt ?? "—"}
                  </TableCell>
                  <TableCell align="right">{item.final_amount}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => navigator.clipboard.writeText(item.wallet)}
                    >
                      {item.wallet}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.status}
                      color={
                        item.status === "SUCCESS"
                          ? "success"
                          : item.status === "DECLINED"
                            ? "error"
                            : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {item.status === "ACCEPTED" && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={busy === item.id}
                          onClick={() => finalize(item.id, "success")}
                        >
                          SUCCESS
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={busy === item.id}
                          onClick={() => finalize(item.id, "decline")}
                        >
                          DECLINED
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
